import type { JSONContent } from '@tiptap/core'
import { isAllowedUri } from '@tiptap/extension-link'
import DOMPurify from 'isomorphic-dompurify'
import type { MentionInterface } from 'modules/community/components/editor/elements/mention/mention.types'

const MENTION_TOKEN_RE = /<@(\d+):([^>]+)>/g

const emptyDoc = (): JSONContent => ({
  type: 'doc',
  content: [{ type: 'paragraph' }],
})

const escapeAttr = (value: string): string =>
  value
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')

type MarkBag = { type: string; attrs?: { href: string } }

const linkMarks = (href: string | undefined): MarkBag[] =>
  href && isAllowedUri(href) ? [{ type: 'link', attrs: { href } }] : []

const textNode = (text: string, marks: MarkBag[]): JSONContent => {
  const node: JSONContent = { type: 'text', text }
  if (marks.length > 0) {
    node.marks = marks.map(mark =>
      mark.attrs ? { type: mark.type, attrs: mark.attrs } : { type: mark.type },
    )
  }
  return node
}

const parseTextWithMentions = (
  textContent: string,
  mentions: MentionInterface[],
  marks: MarkBag[],
): JSONContent[] => {
  const content: JSONContent[] = []
  let lastIndex = 0
  MENTION_TOKEN_RE.lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = MENTION_TOKEN_RE.exec(textContent)) !== null) {
    if (match.index > lastIndex)
      content.push(textNode(textContent.slice(lastIndex, match.index), marks))

    const userId = Number(match[1])
    const label = match[2]
    const mention = mentions.find(item => item.userId === userId && item.displayName === label)
    content.push({
      type: 'mention',
      attrs: { id: userId, label, profileImageUrl: mention?.profileImage ?? '' },
    })
    lastIndex = MENTION_TOKEN_RE.lastIndex
  }

  if (lastIndex < textContent.length) content.push(textNode(textContent.slice(lastIndex), marks))
  if (content.length === 0 && textContent.length === 0) content.push(textNode('', marks))

  return content
}

const serializeInline = (node: JSONContent): string => {
  if (node.type === 'mention') {
    const label = String(node.attrs?.label ?? '')
    return label.trim() ? `<@${node.attrs?.id}:${label}>` : ''
  }
  if (node.type !== 'text') {
    return (node.content ?? []).map(element => serializeInline(element)).join('')
  }
  let text = DOMPurify.sanitize(node.text ?? '')
  if (!text.trim()) return text

  const types = new Set((node.marks ?? []).map(mark => mark.type))
  if (types.has('underline')) text = `<u>${text}</u>`
  if (types.has('italic')) text = `<em>${text}</em>`
  if (types.has('bold')) text = `<strong>${text}</strong>`

  const href = node.marks?.find(mark => mark.type === 'link')?.attrs?.href
  if (!href || !isAllowedUri(String(href))) return text
  return `<a href="${escapeAttr(String(href))}" target="_blank">${text}</a>`
}

export const docToHtml = (doc: JSONContent): string => {
  const blocks = (doc.content ?? []).map(node => {
    if (node.type !== 'paragraph')
      return (node.content ?? []).map(element => serializeInline(element)).join('')
    const children = (node.content ?? [])
      .map(element => serializeInline(element))
      .join('')
      .trim()
    return children ? `<p>${children}</p>` : '<br>'
  })
  while (blocks.at(-1) === '<br>') blocks.pop()
  return blocks.join('') || '<br>'
}

const deserializeElement = (
  el: Node,
  mentions: MentionInterface[],
  inheritedMarks: MarkBag[] = [],
): JSONContent[] => {
  if (el.nodeType === Node.TEXT_NODE) {
    return parseTextWithMentions(el.textContent || '', mentions, inheritedMarks)
  }
  if (!(el instanceof HTMLElement)) return []

  const marks = [...inheritedMarks]
  if (el.nodeName === 'A') marks.push(...linkMarks(el.getAttribute('href') ?? ''))
  if (el.nodeName === 'STRONG' || el.nodeName === 'B') marks.push({ type: 'bold' })
  if (el.nodeName === 'EM' || el.nodeName === 'I') marks.push({ type: 'italic' })
  if (el.nodeName === 'U') marks.push({ type: 'underline' })

  const childNodes = [...el.childNodes].flatMap(child => deserializeElement(child, mentions, marks))

  if (el.nodeName === 'P') {
    return [{ type: 'paragraph', content: childNodes.length > 0 ? childNodes : undefined }]
  }
  if (el.nodeName === 'BR') {
    return [{ type: 'paragraph' }]
  }
  return childNodes
}

export const htmlToDoc = (html: string, mentions: MentionInterface[] = []): JSONContent => {
  if (!('DOMParser' in globalThis)) return emptyDoc()

  const parsed = new DOMParser().parseFromString(html || '', 'text/html')
  const bodyChildren = deserializeElement(parsed.body, mentions)
  const content: JSONContent[] = []
  let pending: JSONContent[] = []

  const flush = (): void => {
    if (pending.length === 0) return
    content.push({ type: 'paragraph', content: pending })
    pending = []
  }

  for (const node of bodyChildren) {
    if (node.type === 'paragraph') {
      flush()
      content.push(node)
    } else {
      pending.push(node)
    }
  }
  flush()

  return content.length > 0 ? { type: 'doc', content } : emptyDoc()
}

const hasContent = (node: JSONContent): boolean => {
  if (node.type === 'mention') return Boolean(String(node.attrs?.label ?? '').trim())
  if (node.text?.trim()) return true
  return (node.content ?? []).some(child => hasContent(child))
}

export const isDocEmpty = (doc: JSONContent): boolean => !hasContent(doc)
